import {
  Component,
  OnInit,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, ValidatorFn, Validators } from '@angular/forms';
import ValidatorUtils from '@core/utils/validator.utils';
import { PendingChanges } from '@core/guards/pending-changes.guard';
import { RestApiService } from '@core/services/rest-api.service';
import { UserRolesAndPermissionsService } from '@core/services/user-roles-and-permissions.service';
import {
  DvDynamicFormUtils,
  DvFormUtils,
  DvRadioGroupLegacyElement,
  DvTabBase,
  FormOf,
} from '@devinco/ui/legacy';
import { SplitterWithGridHelperService } from '@features/vt-grid/services/splitter-with-grid-helper.service';
import { Entity } from '@shared/constants/entity';
import { RolesAndPermissions } from '@shared/constants/roles-and-permissions';
import { VehicleType } from '@shared/constants/vehicle-type';
import { TripType } from '@pages/trips-page/constants';
import { TripInformationTabService } from '@pages/trips-page/services/trip-information-tab.service';
import TripUtils from '@pages/trips-page/shared/trip.utils';
import { MapInput } from '@features/map/interfaces';
import { filter, map, Observable, switchMap, take } from 'rxjs';
import { TripInformationTabForm } from './interfaces';
import { TripsGridService } from '@pages/trips-page/services/trips-grid.service';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { DevincoConnectUserData } from '@core/auth/interfaces';
import { TripDto } from '@pages/trips-page/components/trips-grid/interfaces';
import { DvDynamicFormElement } from '@devinco/ui/legacy';
import { MapId } from '@features/map/constants/map-id.enum';

@Component({
  selector: 'app-trip-information-tab',
  templateUrl: './trip-information-tab.component.html',
  styleUrls: ['./trip-information-tab.component.scss'],
  providers: [TripInformationTabService],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TripInformationTabComponent
  extends DvTabBase
  implements OnInit, PendingChanges
{
  private gridEntityService = inject(SplitterWithGridHelperService);
  private api = inject(RestApiService);
  private tabService = inject(TripInformationTabService);
  private permissionsService = inject(UserRolesAndPermissionsService);
  private readonly tripsGridService = inject(TripsGridService);
  private oidc = inject(OidcSecurityService);

  formGroup: FormGroup<FormOf<TripInformationTabForm>>;
  driverElements: DvDynamicFormElement[];
  tripDetailElements: DvDynamicFormElement[];
  purposeElements: DvDynamicFormElement[];
  tripTypeElements: DvDynamicFormElement[];

  tripMarkers: MapInput[] | undefined;
  isMapVisible$: Observable<boolean>;

  vehicleTypeEnum = VehicleType;
  currentVehicleType: VehicleType;

  readonly userPermissions: typeof RolesAndPermissions = RolesAndPermissions;
  readonly mapId = MapId.TripInfoMap;

  private allElements: DvDynamicFormElement[];
  private initialPrivateTaxableDistance?: number = undefined;
  private requirePurposeOnTripTypeChange = false;
  private initialTripType?: TripType;
  private initialPurpose?: string;

  ngOnInit(): void {
    this.setupVariables();
    this.setupEvents();
  }

  hasChanges = (): boolean => this.formGroup.dirty && this.formGroup.valid;
  pendingChangesSave = (): Observable<boolean> =>
    this.performSave().pipe(map(() => true));

  onSave(): void {
    this.performSave().subscribe(rowData => {
      const { controls } = this.formGroup;
      controls.PrivateOccasionalCount?.setValue(
        rowData.PrivateOccasionalCount,
        { emitEvent: false }
      );
      controls.PrivateOccasionalDistance?.setValue(
        rowData.PrivateOccasionalDistance,
        { emitEvent: false }
      );
    });
  }

  onEntityUpdate(): void {
    this.initialPrivateTaxableDistance = undefined;
    const requestKeys = DvDynamicFormUtils.getKeysForRequest(
      this.allElements
    ).filter(field => field !== 'label');

    this.api
      .getInfo<TripInformationTabForm>(Entity.Trip, this.entityId, [
        ...requestKeys,
        'TripPoints',
        'PrivateTaxableNotAllowed',
        'RequirePurposeOnTripTypeChange',
      ])
      .subscribe(res => {
        if (!Object.keys(res).length) return;
        this.initialPrivateTaxableDistance =
          res.DistancePrivateTaxable || undefined;
        this.toggleTripTypeRadioButtons(
          res.VehicleType,
          !!res.PrivateTaxableNotAllowed,
          !!res.PrivateOccasionalNotAllowed
        );
        this.currentVehicleType = res.VehicleType;
        this.requirePurposeOnTripTypeChange =
          !!res.RequirePurposeOnTripTypeChange;
        this.initialTripType = res.TripType;
        this.initialPurpose = res.Purpose;
        this.formGroup.controls.UserID.setValidators(
          res.IsPrivateTaxable ? [Validators.required] : []
        );

        this.tripTypeElements.find(
          el => el.options.key === 'DistancePrivateTaxable'
        )!.options.hidden = res.VehicleType !== VehicleType.Corporate;
        this.setTripMarkerInput(res);

        DvDynamicFormUtils.setFirstData(this.allElements, this.formGroup, res);
        this.togglePurposeRequired();
      });
  }

  onBaseActions(): void {}
  onEntityCreate(): void {}

  private togglePurposeRequired(): void {
    const purposeControl = this.formGroup.controls.Purpose;
    const purposeElement = this.purposeElements.find(
      el => el.options.key === 'Purpose'
    );
    if (!purposeControl || !purposeElement) return;

    const isRequired =
      this.currentVehicleType === VehicleType.Corporate &&
      this.initialTripType === TripType.PrivateTaxable &&
      this.formGroup.controls.TripType.value === TripType.Corporate &&
      !this.initialPurpose?.trim() &&
      this.requirePurposeOnTripTypeChange;

    const requiredValidators = [
      Validators.required,
      ValidatorUtils.noWhitespaceValidator as ValidatorFn,
    ];
    if (isRequired) {
      purposeControl.addValidators(requiredValidators);
    } else {
      purposeControl.removeValidators(requiredValidators);
    }
    purposeElement.options.hintLeft = isRequired
      ? this.tabService.purposeRequiredHint
      : '';
    purposeControl.updateValueAndValidity();
  }

  private setTripMarkerInput(tripForm: TripInformationTabForm): void {
    this.tripMarkers = TripUtils.getTripMapInput(tripForm);
  }

  private setupVariables(): void {
    this.driverElements = this.tabService.driverElements;
    this.tripDetailElements = this.tabService.tripDetailsElements;
    this.allElements = this.tabService.allElements;
    this.purposeElements = this.tabService.purposeElements;
    this.tripTypeElements = this.tabService.getTripTypeElements();
    this.isMapVisible$ = this.permissionsService.hasPermissions([
      RolesAndPermissions.TripShowInMap,
    ]);

    this.formGroup = DvFormUtils.createFormGroup(this.allElements, true);
  }

  private setupEvents(): void {
    this.listenToTripTypeChanges();
    this.listenToDriverChanges();
    this.tripsGridService.gridRefreshed$
      .pipe(takeUntilDestroyed(this.destroyRef))
      // trigger entityChange event on the same entity
      .subscribe(_ => this.onEntityUpdate());
  }

  private performSave(): Observable<Partial<TripInformationTabForm>> {
    const dirtyValues: Partial<TripInformationTabForm> = {
      ...DvFormUtils.getDirtyValues(this.formGroup),
      // distance should always be included since it can be changed programmatically
      DistancePrivateTaxable: this.formGroup.value.DistancePrivateTaxable,
    };
    const entityId = this.entityId;

    return this.api.upsert(Entity.Trip, entityId, dirtyValues).pipe(
      switchMap(() => this.oidc.getUserData()),
      switchMap((userData: DevincoConnectUserData) => {
        this.formGroup.markAsPristine();
        this.initialTripType = this.formGroup.controls.TripType.value;
        this.initialPurpose = this.formGroup.controls.Purpose?.value;
        this.togglePurposeRequired();
        this.removeTripIfNeeded(dirtyValues, userData.email);
        return this.gridEntityService.refreshRowData(entityId).pipe(
          filter(rowData => !!rowData),
          take(1)
        );
      }),
      switchMap(_ => {
        this.initialPrivateTaxableDistance = undefined;
        return this.api.getInfo<Partial<TripInformationTabForm>>(
          Entity.Trip,
          this.entityId,
          ['PrivateOccasionalDistance', 'PrivateOccasionalCount']
        );
      })
    );
  }

  private removeTripIfNeeded(
    dirtyValues: Partial<TripInformationTabForm>,
    userEmail: string
  ): void {
    if (
      (this.currentVehicleType === VehicleType.Personal ||
        this.currentVehicleType === VehicleType.Company) &&
      dirtyValues.TripType &&
      dirtyValues.TripType === TripType.Personal
    ) {
      const api = this.gridEntityService.getGridApi();
      if ((api.getSelectedNodes()[0].data as TripDto).UserEmail !== userEmail) {
        api
          .getDisplayedRowAtIndex(
            api.getSelectedNodes()[0].rowIndex === 0 ? 1 : 0
          )
          ?.setSelected(true);
      }
      api.refreshServerSide({ route: [], purge: true });
    }
  }

  private toggleTripTypeRadioButtons(
    vehicleType: string,
    privateTaxableNotAllowed: boolean,
    privateOcassionalNotAllowed: boolean
  ): void {
    const tripTypeElement = this.tripTypeElements.find(
      el => el.options.key === 'TripType'
    ) as DvRadioGroupLegacyElement;

    tripTypeElement.options.items =
      vehicleType === VehicleType.Corporate
        ? this.tabService.getCorporateVehicleTypeRadioItems(
            !!privateTaxableNotAllowed
          )
        : this.getPrivateVehicleTypeRadioItems(privateTaxableNotAllowed);

    if (privateOcassionalNotAllowed) {
      tripTypeElement.options.items = tripTypeElement.options.items.filter(
        item => item.value !== TripType.PrivateOccasional
      );
    }
  }

  private getPrivateVehicleTypeRadioItems(privateTaxableNotAllowed = false): {
    value: TripType;
    label: unknown;
  }[] {
    return privateTaxableNotAllowed
      ? this.tabService.personalVehicleTypeRadioItems.filter(
          item => item.value !== TripType.PrivateTaxable
        )
      : this.tabService.personalVehicleTypeRadioItems;
  }

  private listenToTripTypeChanges(): void {
    this.formGroup.controls.TripType.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(tripType => {
        this.togglePurposeRequired();
        const distanceControl = this.formGroup.controls.DistancePrivateTaxable;
        switch (tripType) {
          case TripType.PrivateTaxable:
            distanceControl?.setValue(
              this.initialPrivateTaxableDistance ||
                this.formGroup.controls.Distance.value,
              {
                emitEvent: false,
              }
            );
            if (this.formGroup.disabled) break;
            distanceControl?.enable();
            break;
          case TripType.Corporate:
          case TripType.PrivateOccasional:
            distanceControl?.setValue(undefined, {
              emitEvent: false,
            });
            distanceControl?.disable();
            break;
          default:
            distanceControl?.disable();
            break;
        }
      });
  }

  private listenToDriverChanges(): void {
    const tripTypeControl = this.formGroup.controls.TripType;

    this.formGroup.controls.UserID.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(driverId => {
        if (this.formGroup.disabled) return;
        if (driverId || this.formGroup.controls.IsPrivateTaxable?.value) {
          tripTypeControl.enable();
        } else {
          tripTypeControl.disable();
        }
      });
  }
}
